/**
 * Privacy Policy route
 *
 * Public route — accessible both pre-login and in-app.
 * Uses i18n keys from the `common.legal.*` namespace.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

function PrivacyPage() {
  const { t } = useTranslation("common");

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Back link */}
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("legal.backToHome")}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{t("legal.privacyTitle")}</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("legal.lastUpdated", { date: "2026-02-21" })}
        </p>
      </header>

      {/* Introduction */}
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("legal.privacyIntro")}
      </p>

      {/* Section 1: Data Controller */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.controllerTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.controllerText")}
        </p>
      </section>

      {/* Section 2: Data Collected */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.dataCollectedTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.dataCollectedText")}
        </p>
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
          <li>{t("legal.dataCollectedAccount")}</li>
          <li>{t("legal.dataCollectedHealth")}</li>
          <li>{t("legal.dataCollectedAudio")}</li>
          <li>{t("legal.dataCollectedUsage")}</li>
        </ul>
      </section>

      {/* Section 3: Purposes & Legal Basis */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.purposesTitle")}</h2>
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
          <li>{t("legal.purposesHealthTracking")}</li>
          <li>{t("legal.purposesAI")}</li>
          <li>{t("legal.purposesAccount")}</li>
          <li>{t("legal.purposesSecurity")}</li>
        </ul>
      </section>

      {/* Section 4: AI & Automated Processing */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.aiProcessingTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.aiProcessingText")}
        </p>
      </section>

      {/* Section 5: Data Retention */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.retentionTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.retentionText")}
        </p>
      </section>

      {/* Section 6: Data Sharing */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.sharingTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.sharingText")}
        </p>
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
          <li>{t("legal.sharingInfra")}</li>
          <li>{t("legal.sharingAuth")}</li>
        </ul>
      </section>

      {/* Section 7: Your Rights */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.rightsTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.rightsText")}
        </p>
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
          <li>{t("legal.rightsAccess")}</li>
          <li>{t("legal.rightsRectification")}</li>
          <li>{t("legal.rightsErasure")}</li>
          <li>{t("legal.rightsPortability")}</li>
          <li>{t("legal.rightsRestriction")}</li>
          <li>{t("legal.rightsObject")}</li>
          <li>{t("legal.rightsWithdraw")}</li>
        </ul>
      </section>

      {/* Section 8: International Transfers */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.transfersTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.transfersText")}
        </p>
      </section>

      {/* Section 9: Security */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.securityTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.securityText")}
        </p>
      </section>

      {/* Section 10: Contact & Complaints */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t("legal.contactTitle")}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("legal.contactText")}
        </p>
      </section>

      {/* Footer link to Terms */}
      <footer className="border-t pt-6 pb-4">
        <p className="text-sm text-muted-foreground">
          <Link to="/terms" className="font-medium text-primary underline-offset-4 hover:underline">
            {t("legal.termsOfService")}
          </Link>
        </p>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});
